import { useEffect, useState } from 'react';
import { Loader2, Download, Copy, Check } from 'lucide-react';
import { Modal } from './Modal';
import formStyles from './Form.module.css';
import {
  chargerUrl, generateQrSvg, generateQrPng,
} from '@/lib/qr-generator';
import { generateChargerQrPdf } from '@/lib/qr-pdf';
import styles from './QrModal.module.css';

/**
 * Single-charger QR Code modal (commit 13 of third-party-charger-
 * access ticket). Shows the canonical sticker URL, a vector
 * preview rendered from inline SVG, the URL itself in monospace
 * (with copy affordance), and Download PNG / Download PDF buttons.
 *
 * All generation runs client-side via qrcode + pdf-lib. Triggered
 * from the toolbar button on ChargerDetailPage.
 *
 * No backend route. The URL is non-sensitive — same URL the field
 * sticker carries. No new auth surface.
 */

interface QrModalProps {
  chargerIdentity: string;
  locationId: string;
  locationName: string;
  onClose: () => void;
}

export function QrModal({
  chargerIdentity, locationId, locationName, onClose,
}: QrModalProps) {
  const url = chargerUrl(chargerIdentity, locationId);
  const [svg, setSvg] = useState<string>('');
  const [busy, setBusy] = useState<'png' | 'pdf' | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void generateQrSvg(url).then((s) => {
      if (!cancelled) setSvg(s);
    });
    return () => { cancelled = true; };
  }, [url]);

  const filenameSlug = `${slugify(locationName)}-${chargerIdentity}`;

  const handleDownloadPng = async (): Promise<void> => {
    setBusy('png');
    try {
      const blob = await generateQrPng(url);
      triggerDownload(blob, `${filenameSlug}.png`);
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadPdf = async (): Promise<void> => {
    setBusy('pdf');
    try {
      const blob = await generateChargerQrPdf({ chargerIdentity, locationId, locationName });
      triggerDownload(blob, `${filenameSlug}.pdf`);
    } finally {
      setBusy(null);
    }
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API requires secure context; on http://localhost
      // this works. On insecure intranets it may fail silently —
      // the URL is still visible in the input for manual copy.
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="QR Code" size="md">
      <div className={styles.wrap}>
        <div className={styles.preview}>
          {svg
            // SVG is generated as a complete <svg>…</svg> document
            // string by the qrcode lib; inject directly so the
            // browser renders it inline. The string comes from a
            // local pure function (no external HTML), so no XSS
            // risk introduced — the only variable input is the
            // URL itself, which qrcode encodes as bit-pixels not
            // markup.
            ? <div className={styles.svgHost} dangerouslySetInnerHTML={{ __html: svg }} />
            : <div className={styles.svgHost}><Loader2 size={24} className={styles.spin} /></div>
          }
        </div>

        <p className={styles.identityLabel}>{chargerIdentity}</p>
        <p className={styles.locationLabel}>{locationName}</p>

        <div className={styles.urlRow}>
          <input
            className={`${formStyles.input} ${styles.urlInput}`}
            readOnly
            value={url}
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <button
            type="button"
            className={styles.copyBtn}
            onClick={() => void handleCopy()}
            title="Copy URL to clipboard"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={() => void handleDownloadPng()}
            disabled={busy !== null}
          >
            {busy === 'png'
              ? <><Loader2 size={13} className={styles.spin} /> Building…</>
              : <><Download size={13} /> PNG</>}
          </button>
          <button
            type="button"
            className={formStyles.btnPrimary}
            onClick={() => void handleDownloadPdf()}
            disabled={busy !== null}
          >
            {busy === 'pdf'
              ? <><Loader2 size={13} className={styles.spin} /> Building…</>
              : <><Download size={13} /> PDF</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Trigger a browser download for a blob via a temporary anchor.
 *  URL.createObjectURL holds the blob in memory until revoked, so
 *  we revoke after the click to free it. */
function triggerDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the browser has time to start the download
  // before the URL is freed. 500ms is empirically safe across
  // Chromium / Firefox / Safari.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
}

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'location';
}
